import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { Play, Terminal, FileText, Compass, Server, Wrench, Radio, RotateCw, ArrowUpDown, Square, Inbox} from "lucide-react";
import { useDispatch } from "react-redux";
import {
  fetchProjectServers,
  k8sListDeployments,
  k8sListNamespaces,
  k8sListPods,
  k8sListServices,
  k8sPodLogs,
  k8sRolloutRestart,
  k8sScaleDeployment,
} from "../../redux/actionCreator";
import "./clusterPanel.scss";

/**
 * Live Kubernetes view embedded in the Deploy page — Deployments / Pods /
 * Services for one of the project's servers (whichever one has the cluster).
 * Reuses every K8s endpoint already wired for the per-server K8s panel.
 *
 * The intent: stay on Deploy. After pushing a build, you don't need to
 * navigate to a different page to confirm the deployment actually rolled
 * out, scaled, or that the service has an IP.
 */
const TABS = [
  { key: "deployments", label: "Deployments" },
  { key: "pods", label: "Pods" },
  { key: "services", label: "Services" },
];

export default function ClusterPanel({ projectId, environment, envServerIds }) {
  const dispatch = useDispatch();
  // `allServers` is the full project server list; `servers` (below) is the
  // subset visible in the dropdown after filtering to the active stage's
  // target set. We keep both so a stage with no apps still has a sensible
  // "nothing here" message instead of a broken empty dropdown.
  const [allServers, setAllServers] = useState([]);
  const [serverId, setServerId] = useState("");
  const [namespaces, setNamespaces] = useState([]);
  const [namespace, setNamespace] = useState("");
  const [tab, setTab] = useState("deployments");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  // Per-pod container pick (only used for multi-container pods).
  // Keyed by `${namespace}/${pod}` so it survives row re-orderings.
  const [pickedContainers, setPickedContainers] = useState({});

  // Load the project's servers; the dropdown then narrows to whichever
  // are referenced by an app at the current stage (via envServerIds).
  useEffect(() => {
    if (!projectId) return;
    dispatch(fetchProjectServers(projectId)).then((res) => {
      const list = res?.data || [];
      setAllServers(list);
    });
  }, [projectId, dispatch]);

  // Filtered subset for the dropdown — only servers used by apps on the
  // active stage. When envServerIds is null/undefined we treat that as
  // "no filter" and fall back to the full list (e.g. standalone usage).
  const servers = useMemo(() => {
    if (!Array.isArray(envServerIds)) return allServers;
    const set = new Set(envServerIds.map(String));
    return allServers.filter((s) => set.has(String(s._id)));
  }, [allServers, envServerIds]);

  // Auto-pick the first available server in the filtered list when the
  // current pick isn't (or no longer is) in scope for this stage.
  useEffect(() => {
    if (!servers.length) { setServerId(""); return; }
    if (!serverId || !servers.some((s) => String(s._id) === String(serverId))) {
      setServerId(String(servers[0]._id));
    }
  }, [servers, serverId]);

  useEffect(() => {
    if (!serverId) return;
    dispatch(k8sListNamespaces(projectId, serverId)).then((res) => {
      if (res?.status) setNamespaces(res.data || []);
    });
  }, [projectId, serverId, dispatch]);

  // When the active stage or the selected server changes, default the
  // namespace dropdown to the server's per-stage namespace mapping (or
  // its single legacy `k8s_namespace`, or kubectl's default "" if both
  // are blank). Only fires when the operator hasn't manually picked a
  // namespace yet, so a deliberate switch isn't overridden mid-session.
  const stageDefaultedRef = useRef("");
  useEffect(() => {
    if (!serverId) return;
    const srv = allServers.find((s) => String(s._id) === String(serverId));
    if (!srv) return;
    const fromMap = srv.k8s_namespaces?.[environment] || "";
    const fallback = srv.k8s_namespace || "";
    const candidate = fromMap || fallback || "";
    // Re-apply when stage changes — we track the last value we wrote so a
    // user override (typing into the picker) sticks until they switch stage.
    if (stageDefaultedRef.current !== `${serverId}/${environment}`) {
      stageDefaultedRef.current = `${serverId}/${environment}`;
      setNamespace(candidate);
    }
  }, [serverId, environment, allServers]);

  const load = useCallback(async () => {
    if (!projectId || !serverId) return;
    setLoading(true);
    setErr(null);
    let res;
    if (tab === "deployments") {
      res = await dispatch(k8sListDeployments(projectId, serverId, namespace));
    } else if (tab === "pods") {
      res = await dispatch(k8sListPods(projectId, serverId, namespace));
    } else {
      res = await dispatch(k8sListServices(projectId, serverId, namespace));
    }
    setLoading(false);
    if (res?.status) setRows(res.data || []);
    else { setRows([]); setErr(res?.message || "Failed to load"); }
  }, [dispatch, projectId, serverId, namespace, tab]);

  useEffect(() => { load(); }, [load]);

  const selectedServer = useMemo(
    () => servers.find((s) => String(s._id) === String(serverId)) || null,
    [servers, serverId]
  );

  const restartDeployment = (d) => {
    setBusy(d.name);
    dispatch(k8sRolloutRestart(projectId, serverId, d.name, d.namespace, () => {
      setBusy(null);
      load();
    }));
  };
  const scaleDeployment = (d, replicas) => {
    setBusy(d.name);
    dispatch(k8sScaleDeployment(projectId, serverId, d.name, replicas, d.namespace, () => {
      setBusy(null);
      load();
    }));
  };
  const stopDeployment = (d) => {
    if (!window.confirm(`Stop ${d.name}? Scales to 0 replicas.`)) return;
    scaleDeployment(d, 0);
  };
  const startDeployment = (d) => scaleDeployment(d, 1);
  const promptScale = (d) => {
    const v = window.prompt(`Scale ${d.name} to how many replicas?`, String(d.desired ?? 1));
    if (v === null) return;
    const n = parseInt(v, 10);
    if (!Number.isInteger(n) || n < 0) { alert("Enter a non-negative integer"); return; }
    scaleDeployment(d, n);
  };

  // ── Pod / Deployment terminal & logs ──────────────────────────
  //
  // Opens a new tab pointing at /terminal/<projectId>/<serverId>?pod=…&namespace=…
  // The WS terminal server picks up `pod` and runs:
  //   kubectl exec -it -n <ns> <pod> -- /bin/sh -l
  //
  // For a Deployment the pod field is `deployment/<name>` — kubectl
  // resolves that to the first matching pod, so the operator doesn't
  // need to know the pod hash. Pair with the per-stage namespace
  // (already filtered into the table rows) and the right shell pops
  // up regardless of which microservice they clicked.
  const openShell = (podOrRef, ns, container) => {
    if (!selectedServer) return;
    const qs = new URLSearchParams({
      label: `${selectedServer.label} · ${podOrRef}`,
      host: `${selectedServer.username}@${selectedServer.host}:${selectedServer.port}`,
      pod: podOrRef,
    });
    if (ns) qs.set("namespace", ns);
    if (container) qs.set("container", container);
    window.open(
      `/terminal/${projectId}/${serverId}?${qs.toString()}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  // Logs modal state. `containers` is the multi-container list (single
  // entry for the common case). `follow` toggles the live-tail poll
  // (re-fetch + replace every POLL_MS); we keep the timer in a ref so
  // the modal close path can clear it cleanly without re-renders.
  const POLL_MS = 2500;
  const [logs, setLogs] = useState(null); // { pod, namespace, containers[], container, tail, loading, text, follow }
  const logsBodyRef = useRef(null);
  const pollRef = useRef(null);

  // Internal fetch — used by both the initial open and the follow poll.
  // Doesn't toggle the spinner on follow ticks (avoids the modal
  // flickering when the content is just being refreshed).
  const fetchLogs = useCallback(async (pod, ns, container, tail, opts = {}) => {
    const silent = !!opts.silent;
    if (!silent) {
      setLogs((cur) => cur ? { ...cur, loading: true } : cur);
    }
    const res = await dispatch(k8sPodLogs(projectId, serverId, pod, { namespace: ns, container, tail }));
    setLogs((cur) => {
      // Stop if the modal was closed (or switched to a different pod)
      // while the request was in flight — don't overwrite the new state.
      if (!cur || cur.pod !== pod || cur.namespace !== ns || cur.container !== container) return cur;
      return {
        ...cur,
        loading: false,
        text: res?.status
          ? (res.data?.log || "(no output)")
          : (res?.message || "(failed to fetch)"),
      };
    });
  }, [dispatch, projectId, serverId]);

  // Open the modal for a pod. `containers` comes from the pod row
  // (already populated by the backend's listPods); we default to the
  // first one so single-container pods need no picking.
  const openLogs = useCallback((pod, ns, containers, container) => {
    const containerList = Array.isArray(containers) && containers.length ? containers : (container ? [container] : []);
    const chosen = container || containerList[0] || "";
    const tail = 500;
    setLogs({ pod, namespace: ns, containers: containerList, container: chosen, tail, loading: true, text: "", follow: false });
    fetchLogs(pod, ns, chosen, tail);
  }, [fetchLogs]);

  // Toggle follow-mode. ON → start a setInterval that re-fetches
  // silently every POLL_MS and snaps the modal to the bottom if the
  // user hasn't scrolled up. OFF → clear the interval.
  const setFollow = (on) => {
    setLogs((cur) => cur ? { ...cur, follow: on } : cur);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (on) {
      pollRef.current = setInterval(() => {
        setLogs((cur) => {
          if (!cur || !cur.follow) return cur;
          fetchLogs(cur.pod, cur.namespace, cur.container, cur.tail, { silent: true });
          return cur;
        });
      }, POLL_MS);
    }
  };

  // Stop polling when modal closes / unmounts.
  useEffect(() => {
    if (!logs) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [logs]);

  // Auto-scroll to bottom on each text update IF the user is already
  // near the bottom. Lets them scroll up to inspect old output without
  // being yanked back down by the next poll.
  useEffect(() => {
    const el = logsBodyRef.current;
    if (!el || !logs?.text) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [logs?.text]);

  if (!servers.length) {
    // Distinct messages so the operator can see WHY there's nothing here:
    // either no servers exist on the project (set them up first) or the
    // active stage has no app pointing at one of them yet.
    const filteredOut = Array.isArray(envServerIds) && allServers.length > 0;
    return (
      <div className="cluster-panel">
        <div className="cluster-head">
          <div>
            <h5 className="m-0 d-inline-flex align-items-center gap-2">
              <Compass size={18} /> Cluster
              {environment && (
                <span
                  className="ms-2 badge"
                  style={{
                    background: environment === "production" ? "#fee2e2" : environment === "staging" ? "#fef3c7" : "#dbeafe",
                    color: environment === "production" ? "#b91c1c" : environment === "staging" ? "#b45309" : "#2563eb",
                    fontWeight: 600,
                  }}
                >
                  {environment}
                </span>
              )}
            </h5>
          </div>
        </div>
        <div className="text-center py-4 text-muted">
          {filteredOut ? (
            <>
              <div style={{ fontSize: 32 }}><Inbox size={28} strokeWidth={1.4} /></div>
              <p className="mb-1">No <strong>{environment}</strong> app on this project references a server.</p>
              <small>Edit an app on this stage and tick at least one server under "Target servers".</small>
            </>
          ) : (
            <>
              <div style={{ fontSize: 32 }} className="d-flex justify-content-center"><Server size={32} strokeWidth={1.2} /></div>
              <p className="mb-0">No servers on this project yet.</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="cluster-panel">
      <div className="cluster-head">
        <div>
          <h5 className="m-0 d-inline-flex align-items-center gap-2">
            <Compass size={18} /> Cluster
            {environment && (
              <span
                className="ms-2 badge"
                style={{
                  background: environment === "production" ? "#fee2e2" : environment === "staging" ? "#fef3c7" : "#dbeafe",
                  color: environment === "production" ? "#b91c1c" : environment === "staging" ? "#b45309" : "#2563eb",
                  fontWeight: 600,
                }}
                title="The active environment from the page header"
              >
                {environment}
              </span>
            )}
          </h5>
          <small className="text-muted">Live deployments, pods &amp; services on the project's server.</small>
        </div>
        <div className="cluster-pickers">
          <Form.Select size="sm" value={serverId} onChange={(e) => setServerId(e.target.value)} style={{ minWidth: 220 }}>
            {servers.map((s) => (
              <option key={s._id} value={s._id}>{s.label} ({s.username}@{s.host})</option>
            ))}
          </Form.Select>
          {(() => {
            // The stage default (from server.k8s_namespaces[env] → fallback
            // server.k8s_namespace). Surfaced in the placeholder so the
            // operator knows what kubectl will use without picking anything.
            const srv = allServers.find((s) => String(s._id) === String(serverId));
            const stageDefault = srv?.k8s_namespaces?.[environment] || srv?.k8s_namespace || "";
            return (
              <Form.Select
                size="sm"
                value={namespace}
                onChange={(e) => setNamespace(e.target.value)}
                style={{ minWidth: 200 }}
                title={stageDefault ? `Stage default for ${environment}: ${stageDefault}` : ""}
              >
                <option value="">
                  {stageDefault ? `— stage default: ${stageDefault} —` : "— all / default ns —"}
                </option>
                {namespaces.map((n) => <option key={n} value={n}>{n}</option>)}
              </Form.Select>
            );
          })()}
          <Button size="sm" variant="light" onClick={load} disabled={loading}>
            {loading ? <Spinner size="sm" animation="border" /> : "↻"}
          </Button>
        </div>
      </div>

      <div className="cluster-tabs">
        {TABS.map((t) => (
          <button key={t.key} type="button" className={tab === t.key ? "on" : ""} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {err && <div className="alert alert-warning small mb-2">{err}</div>}

      <div className="cluster-table-wrap">
        {tab === "deployments" && (
          <table className="cluster-table">
            <thead><tr><th>Name</th><th>Ready</th><th>Image(s)</th><th className="text-end">Actions</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={4} className="empty"><Spinner size="sm" animation="border" /></td></tr>
                : rows.length === 0 ? <tr><td colSpan={4} className="empty">No deployments.</td></tr>
                : rows.map((d) => {
                    const healthy = d.ready === d.desired && d.desired > 0;
                    return (
                      <tr key={`${d.namespace}/${d.name}`}>
                        <td><div className="row-name">{d.name}</div><div className="row-sub">{d.namespace}</div></td>
                        <td><span className={`badge bg-${healthy ? "success" : d.ready > 0 ? "warning" : "secondary"}`}>{d.ready}/{d.desired}</span></td>
                        <td><code className="small">{(d.images || []).join(", ")}</code></td>
                        <td className="text-end actions">
                          <Button
                            size="sm" variant="outline-dark"
                            disabled={d.ready === 0}
                            title={d.ready === 0 ? "No ready pod to exec into" : "kubectl exec into the first pod"}
                            onClick={() => openShell(`deployment/${d.name}`, d.namespace)}
                          ><Terminal size={12} /></Button>
                          <Button size="sm" variant="outline-warning" disabled={busy === d.name} onClick={() => restartDeployment(d)} title="kubectl rollout restart">
                            {busy === d.name ? <Spinner size="sm" animation="border" /> : <RotateCw size={12} />}
                          </Button>
                          <Button size="sm" variant="light" disabled={busy === d.name} onClick={() => promptScale(d)} title="Scale replicas"><ArrowUpDown size={12} /></Button>
                          {d.desired > 0
                            ? <Button size="sm" variant="outline-danger" disabled={busy === d.name} onClick={() => stopDeployment(d)} title="Scale to 0"><Square size={12} fill="currentColor" /></Button>
                            : <Button size="sm" variant="outline-success" disabled={busy === d.name} onClick={() => startDeployment(d)} title="Scale to 1"><Play size={12} /></Button>}
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        )}

        {tab === "pods" && (
          <table className="cluster-table">
            <thead><tr><th>Pod</th><th>Ready</th><th>Phase</th><th>Restarts</th><th>Node</th><th>IP</th><th className="text-end">Actions</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="empty"><Spinner size="sm" animation="border" /></td></tr>
                : rows.length === 0 ? <tr><td colSpan={7} className="empty">No pods.</td></tr>
                : rows.map((p) => {
                    // Shell/logs only useful for a pod that's actually
                    // running. Pending/Failed/Unknown won't accept exec,
                    // and logs from a never-started container come back
                    // empty.
                    const canShell = p.phase === "Running";
                    const cs = Array.isArray(p.containers) ? p.containers : [];
                    const multi = cs.length > 1;
                    // For multi-container pods (sidecars, init+main, etc.)
                    // the picked container is held per-pod here. Single-
                    // container pods skip the picker entirely so the
                    // common case stays one-click.
                    const picked = pickedContainers[`${p.namespace}/${p.name}`] || cs[0] || "";
                    return (
                      <tr key={`${p.namespace}/${p.name}`}>
                        <td><div className="row-name">{p.name}</div><div className="row-sub">{p.namespace}</div></td>
                        <td>{p.ready}</td>
                        <td>
                          <span className={`badge bg-${p.phase === "Running" ? "success" : p.phase === "Pending" ? "warning" : "secondary"}`}>{p.phase || "—"}</span>
                          {p.reason && <div className="badge bg-danger mt-1" title={p.reason_message || p.reason} style={{ whiteSpace: "normal", maxWidth: 200 }}>{p.reason}</div>}
                        </td>
                        <td>{p.restarts ?? 0}</td>
                        <td className="small">{p.node}</td>
                        <td><code className="small">{p.podIp || "—"}</code></td>
                        <td className="text-end actions">
                          {multi && (
                            <Form.Select
                              size="sm"
                              value={picked}
                              onChange={(e) => setPickedContainers((m) => ({ ...m, [`${p.namespace}/${p.name}`]: e.target.value }))}
                              style={{ width: "auto", display: "inline-block", minWidth: 110 }}
                              title="Container to target for exec / logs"
                            >
                              {cs.map((c) => <option key={c} value={c}>{c}</option>)}
                            </Form.Select>
                          )}
                          <Button
                            size="sm" variant="outline-dark"
                            disabled={!canShell}
                            title={canShell
                              ? `kubectl exec -it ${multi ? `-c ${picked} ` : ""}-- /bin/sh`
                              : `Pod is ${p.phase} — cannot exec`}
                            onClick={() => openShell(p.name, p.namespace, multi ? picked : undefined)}
                          ><Terminal size={12} /></Button>
                          <Button
                            size="sm" variant="outline-primary"
                            title="kubectl logs (last 500 lines)"
                            onClick={() => openLogs(p.name, p.namespace, cs, multi ? picked : (cs[0] || ""))}
                          ><FileText size={12} /></Button>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        )}

        {tab === "services" && (
          <table className="cluster-table">
            <thead><tr><th>Service</th><th>Type</th><th>ClusterIP</th><th>External</th><th>Ports</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={5} className="empty"><Spinner size="sm" animation="border" /></td></tr>
                : rows.length === 0 ? <tr><td colSpan={5} className="empty">No services.</td></tr>
                : rows.map((s) => (
                    <tr key={`${s.namespace}/${s.name}`}>
                      <td><div className="row-name">{s.name}</div><div className="row-sub">{s.namespace}</div></td>
                      <td><span className="badge bg-secondary">{s.type}</span></td>
                      <td><code className="small">{s.cluster_ip || "—"}</code></td>
                      <td><code className="small">{s.external_ip || "—"}</code></td>
                      <td><code className="small">{(s.ports || []).join(", ") || "—"}</code></td>
                    </tr>
                  ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedServer && (
        <div className="text-muted small mt-2">
          Source: <code>{selectedServer.username}@{selectedServer.host}</code>
          {" — "}If you don't see Kubernetes resources here, run <strong className="d-inline-flex align-items-center gap-1"><Wrench size={12} /> Setup → Install k3s</strong> on that server first.
        </div>
      )}

      {/* Pod logs modal — supports container picking (multi-container
          pods like init+main or main+sidecar) and a Follow toggle that
          polls every POLL_MS and snaps to the bottom while the user
          isn't scrolled up. */}
      <Modal show={!!logs} onHide={() => { setFollow(false); setLogs(null); }} size="xl" centered>
        <Modal.Header closeButton>
          <div className="d-flex flex-column" style={{ flex: 1 }}>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h6 className="m-0 d-inline-flex align-items-center gap-2">
                <FileText size={16} /> {logs?.pod || ""}
              </h6>
              {logs?.follow && (
                <span className="badge bg-success" title={`Refreshing every ${POLL_MS}ms`}>
                  ● LIVE
                </span>
              )}
              {(logs?.containers || []).length > 1 && (
                <Form.Select
                  size="sm"
                  style={{ width: "auto" }}
                  value={logs.container}
                  onChange={(e) => {
                    const next = e.target.value;
                    setLogs((cur) => cur ? { ...cur, container: next, text: "", loading: true } : cur);
                    fetchLogs(logs.pod, logs.namespace, next, logs.tail);
                  }}
                  title="Switch container"
                >
                  {(logs.containers || []).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </Form.Select>
              )}
              <Form.Select
                size="sm"
                style={{ width: "auto" }}
                value={logs?.tail || 500}
                onChange={(e) => {
                  const next = parseInt(e.target.value, 10) || 500;
                  setLogs((cur) => cur ? { ...cur, tail: next, loading: true } : cur);
                  fetchLogs(logs.pod, logs.namespace, logs.container, next);
                }}
                title="How many trailing lines to fetch"
              >
                <option value={100}>tail 100</option>
                <option value={500}>tail 500</option>
                <option value={1000}>tail 1000</option>
                <option value={5000}>tail 5000</option>
              </Form.Select>
            </div>
            <small className="text-muted">
              {logs?.namespace ? `${logs.namespace} · ` : ""}{logs?.container ? `container=${logs.container} · ` : ""}last {logs?.tail || 500} lines
            </small>
          </div>
        </Modal.Header>
        <Modal.Body ref={logsBodyRef} style={{ maxHeight: "70vh", overflow: "auto", background: "#0b0f14" }}>
          {logs?.loading && !logs?.text ? (
            <div className="text-center text-muted py-4"><Spinner size="sm" animation="border" /></div>
          ) : (
            <pre
              className="m-0"
              style={{ color: "#d6deeb", fontSize: 12, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >
              {logs?.text || ""}
            </pre>
          )}
        </Modal.Body>
        <Modal.Footer>
          {logs?.pod && (
            <>
              <Form.Check
                type="switch"
                id="logs-follow-switch"
                label={<span className="d-inline-flex align-items-center gap-1"><Radio size={12} /> Follow ({(POLL_MS / 1000).toFixed(1)}s)</span>}
                checked={!!logs?.follow}
                onChange={(e) => setFollow(e.target.checked)}
              />
              <Button
                size="sm" variant="outline-secondary"
                onClick={() => fetchLogs(logs.pod, logs.namespace, logs.container, logs.tail)}
                disabled={logs?.loading}
              >↻ Refresh</Button>
            </>
          )}
          <Button size="sm" variant="secondary" onClick={() => { setFollow(false); setLogs(null); }}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
